import {
  addMonths,
  addYears,
  endOfMonth,
  endOfWeek,
  endOfYear,
  format,
  getDaysInMonth,
  isAfter,
  isBefore,
  isValid,
  parseISO,
  startOfMonth,
  startOfWeek,
  startOfYear,
  subDays,
  subMonths,
} from 'date-fns';

export const SCHEDULE_MODES = ['week', 'month', 'year'] as const;
export type ScheduleMode = (typeof SCHEDULE_MODES)[number];

export const QUICK_RANGES = [
  'last7Days',
  'last30Days',
  'last12Months',
  'currentMonth',
  'currentYear',
  'next12Months',
] as const;
export type QuickRange = (typeof QUICK_RANGES)[number];

export const DATE_RANGE_OPTIONS = [...QUICK_RANGES, 'custom'] as const;
export type DateRangeOption = (typeof DATE_RANGE_OPTIONS)[number];

export interface IsoDateRange {
  from: string;
  to: string;
}

export type DateRangeError = 'endBeforeStart' | 'overFiveYears';

const WEEK_STARTS_MONDAY = { weekStartsOn: 1 } as const;
const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

export const toIsoDate = (date: Date) => format(date, 'yyyy-MM-dd');

export const fromIsoDate = (isoDate: string) => parseISO(isoDate);

export const isIsoDate = (value: string) => ISO_DATE.test(value) && isValid(parseISO(value));

export const isQuickRange = (value: string): value is QuickRange => QUICK_RANGES.some((range) => range === value);

const span = (from: Date, to: Date): IsoDateRange => ({ from: toIsoDate(from), to: toIsoDate(to) });

export const resolveQuickRange = (range: QuickRange, mode: ScheduleMode, today: Date): IsoDateRange => {
  const byWeek = mode === 'week';

  switch (range) {
    case 'last7Days':
      return span(subDays(today, 7), today);
    // Kept from the legacy app: the span is the current month's length, not a fixed 30 days.
    case 'last30Days':
      return span(subDays(today, getDaysInMonth(today)), today);
    case 'last12Months': {
      const start = subMonths(today, 12);
      return byWeek
        ? span(startOfWeek(start, WEEK_STARTS_MONDAY), endOfWeek(today, WEEK_STARTS_MONDAY))
        : span(startOfMonth(start), endOfMonth(today));
    }
    case 'currentMonth':
      return span(startOfMonth(today), endOfMonth(today));
    case 'currentYear':
      return span(startOfYear(today), endOfYear(today));
    case 'next12Months': {
      const end = addMonths(today, 12);
      return byWeek
        ? span(startOfWeek(today, WEEK_STARTS_MONDAY), endOfWeek(end, WEEK_STARTS_MONDAY))
        : span(startOfMonth(today), endOfMonth(end));
    }
  }
};

export const validateDateRange = ({ from, to }: IsoDateRange): DateRangeError | undefined => {
  const start = fromIsoDate(from);
  const end = fromIsoDate(to);

  if (isBefore(end, start)) {
    return 'endBeforeStart';
  }
  if (isAfter(end, addYears(start, 5))) {
    return 'overFiveYears';
  }
  return undefined;
};
